"use client";
import {
  MainMetaMap,
  MainMetaMapSubject,
  MainMetaMapSubTheme,
  MainMetaMapSubTopic,
  MainMetaMapTopic,
} from "@tsTypes/meta-schema";
import { createContext, ReactNode, useContext } from "react";

interface MainMetaMapContextType {
  metaMap: MainMetaMap;
}

const MainMetaMapContext = createContext<MainMetaMapContextType | null>(null);

export function CoreDataProvider({
  children,
  metaMap,
}: {
  children: ReactNode;
  metaMap: MainMetaMap;
}) {
  return (
    <MainMetaMapContext.Provider value={{ metaMap }}>
      {children}
    </MainMetaMapContext.Provider>
  );
}

export function useMainMetaMap() {
  const context = useContext(MainMetaMapContext);
  if (!context) {
    throw new Error("useMainMetaMap must be used within MainMetaMapProvider");
  }
  return context.metaMap;
}

export function useSubjectMeta(subjectDocumentId: string) {
  const metaMap = useMainMetaMap();
  return metaMap[subjectDocumentId] as MainMetaMapSubject;
}

export function useTopicMeta(topicDocumentId: string) {
  const metaMap = useMainMetaMap();
  return metaMap[topicDocumentId] as MainMetaMapTopic;
}

export function useSubTopicMeta(subTopicDocumentId: string) {
  const metaMap = useMainMetaMap();
  return metaMap[subTopicDocumentId] as MainMetaMapSubTopic;
}

export function useMultipleSubTopicMeta(subTopicDocumentIds: string[]) {
  const metaMap = useMainMetaMap();
  return subTopicDocumentIds.reduce(
    (acc: { [documentId: string]: MainMetaMapSubTopic }, documentId) => {
      acc[documentId] = metaMap[documentId] as MainMetaMapSubTopic;
      return acc;
    },
    {},
  );
}

export function useSubThemeMeta(subThemeDocumentId: string) {
  const metaMap = useMainMetaMap();
  return metaMap[subThemeDocumentId] as MainMetaMapSubTheme;
}
